<template>
  <BackendUploaderBase
    ref="uploader"
    v-bind="{ multiple, accept, autoUpload, url, factory, capture, disable, category }"
    hide-upload-btn
    no-thumbnails
    flat
    class="invisible-uploader full-width q-pa-md"
    @start="$emit('start')"
    @finish="$emit('finish')"
    @added="$emit('added', $event)"
    @uploaded="$emit('uploaded', $event)"
  >
    <template #header="scope">
      <div
        class="drag-area bg-theme shadow-2"
        :class="{'into-category': !!category, 'disabled no-pointer-events': disable || scope.isBusy || scope.isUploading}"
      >
        <q-avatar
          v-if="icon !== false"
          :icon="icon"
          font-size="3rem"
          size="4rem"
          text-color="blue-5"
        />

        <div class="text-blue-grey-8 text-h6">
          Drop your files here, or <span class="text-blue-7">browse</span>
        </div>

        <div v-if="strDefaultSublabel" class="text-blue-grey-4">
          {{ strDefaultSublabel }}
        </div>

        <q-uploader-add-trigger />
      </div>

      <q-toolbar
        v-if="showActionBar"
        class="q-px-none text-black uploader-actions"
        :class="{ 'uploader-actions--fill': $q.platform.is.mobile }"
      >
        <q-btn
          v-if="$q.platform.is.mobile"
          no-caps
          color="blue"
          icon="mdi-camera"
          label="Take Photo"
        >
          <q-uploader-add-trigger
            accept="image/*"
            capture="environment"
          />
        </q-btn>
        <slot name="actions" />
      </q-toolbar>
    </template>

    <template #list="scope">
      <template v-if="scope.files.length > 0">
        <q-linear-progress
          v-if="scope.isBusy"
          :value="scope.checkingImages ? scope.checkingProgress : scope.uploadProgress"
          :color="scope.checkingImages ? 'orange' : 'blue'"
          stripe
          class="q-mt-sm"
          style="height: 5px;"
        />

        <q-list separator class="text-black">
          <q-item-label
            v-if="!autoUpload"
            class="text-bold"
            header
          >
            Pending Files:
          </q-item-label>
          <q-item
            v-for="(file, key) in scope.files"
            :key="key"
          >
            <q-item-section>
              <q-item-label class="full-width ellipsis">
                {{ file.name || 'CapturedImage.jpg' }}
              </q-item-label>
            </q-item-section>
            <q-item-section
              v-if="!autoUpload && !scope.loading"
              side
            >
              <q-btn
                flat
                color="red"
                round
                dense
                icon="close"
                @click="scope.removeFile(file)"
              />
            </q-item-section>
          </q-item>
        </q-list>

        <q-toolbar v-if="!autoUpload && !hideSubmitBtn">
          <q-space />
          <q-btn
            label="Submit"
            color="green"
            icon="mdi-upload"
            :loading="scope.loading"
            @click="scope.upload"
          />
        </q-toolbar>
      </template>
    </template>
  </BackendUploaderBase>
</template>

<script>
import BackendUploaderBase from 'components/partials/uploader/BackendUploaderBase'
import componentMixin from 'components/partials/uploader/componentMixin'

export default {
  components: {
    BackendUploaderBase
  },
  mixins: [componentMixin],
  props: {
    icon: {
      type: [Boolean, String],
      default: 'mdi-file-image-outline'
    },
    hideSublabel: {
      type: [Boolean, String],
      default: 'Supports JPG / PNG images, documents and PDFs.'
    },
    showFileMaximum: {
      type: Boolean,
      default: true
    },
    hideSubmitBtn: {
      type: Boolean,
      default: false
    },
    hideActionBar: {
      type: Boolean,
      default: false
    }
  },
  emits: ['start', 'finish', 'uploaded', 'added'],
  computed: {
    showActionBar () {
      return !this.hideActionBar && (this.$q.platform.is.mobile || this.$slots.actions)
    },
    strDefaultSublabel () {
      if (this.hideSublabel === true) {
        return null
      }

      const fileSizeLimit = this.$store.getters.getSystemSetting('max_file_size') || 0
      const str = this.hideSublabel
      if (this.showFileMaximum && fileSizeLimit > 0) {
        return `${str} Maximum ${fileSizeLimit}MB per file.`
      }
      return str
    }
  }
}
</script>

<style scoped lang="scss">
.drag-area {
  position: relative;
  border: 2px dashed $blue-7;
  width: 100%;
  text-align: center;
  padding: 20px 5px;
  border-radius: 4px;
}

.uploader-actions {
  // The buttons sat flush against the drop area, which read as part of it.
  margin-top: 0.75rem;
  gap: 0.5rem;
  min-height: unset;
}

// A phone has room for one row of buttons and no more, so let them use all of
// it rather than huddling against the left edge. Import ZIP arrives through a
// slot, so it carries its own scope id -- hence :deep() rather than a plain
// child selector, which would only ever match the button declared here.
.uploader-actions--fill {
  :deep(.q-btn),
  :deep(.invisible-uploader) {
    flex: 1 1 0;
    min-width: 0;
    // Spacing is the toolbar's gap now; a margin would break the even split.
    margin-right: 0;
  }

  :deep(.invisible-uploader .q-btn) {
    width: 100%;
  }
}
</style>
